// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Spinner } from '@loa/ui-components';
import { Card } from '../Card';
import { RiskResultCard } from './RiskResultCard';
import {
  profileFormApi,
  type FactFindQuestion,
  type FactFindSchema,
  type ProfileFormType,
  type ProfileFormVersion,
  type RiskResult,
} from '../../api/profileFormApi';
import { clientApi } from '../../api/clientApi';
import {
  buildSectionViews,
  isSectionedSchema,
  type DisplayBlock,
  type SectionView,
} from './sectionedAnswers';

interface Props {
  profileId: string;
  formType: ProfileFormType;
  /** Render the risk score + flags card above the answers (scored forms only). */
  showResult?: boolean;
}

/**
 * Group flat rows by the schema's `topic` metadata (Deep Dive). Rows without
 * a topic fall under 'General'; returns a single anonymous group when NO row
 * carries a topic so plain flat forms keep the ungrouped list. Exported for
 * direct unit testing.
 */
export function groupFlatRowsByTopic(
  rows: Array<{ q: FactFindQuestion; value: unknown }>,
): Array<{ topic: string | null; rows: Array<{ q: FactFindQuestion; value: unknown }> }> {
  if (!rows.some((r) => r.q.topic)) {
    return rows.length > 0 ? [{ topic: null, rows }] : [];
  }
  const groups = new Map<string, Array<{ q: FactFindQuestion; value: unknown }>>();
  for (const row of rows) {
    const topic = row.q.topic ?? 'General';
    if (!groups.has(topic)) groups.set(topic, []);
    groups.get(topic)!.push(row);
  }
  return Array.from(groups, ([topic, groupRows]) => ({ topic, rows: groupRows }));
}

/** Display string for one answered value (exported for direct unit testing). */
export function displayAnswer(q: FactFindQuestion, value: unknown): string {
  if (Array.isArray(value)) {
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (q.type === 'boolean') {
    return String(value) === 'true' ? 'Yes' : 'No';
  }
  if (q.type === 'enum') {
    const opt = (q.options || []).find((o) => (typeof o === 'string' ? o : o.value) === String(value));
    if (opt && typeof opt === 'object') {
      return opt.label || opt.value;
    }
  }
  return String(value);
}

function RowList({ rows, testIdPrefix }: Readonly<{ rows: DisplayBlock['rows']; testIdPrefix: string }>) {
  return (
    <dl className="grid grid-cols-[minmax(160px,280px)_1fr] gap-x-4 gap-y-2 m-0">
      {rows.map((row) => (
        <React.Fragment key={`${row.label}-${row.value}`}>
          <dt className="text-body-03 text-[#6b7280]">{row.label}</dt>
          <dd className="text-body-03 m-0" data-testid={`${testIdPrefix}-${row.label}`}>
            {row.value}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function SectionBlocks({ view }: Readonly<{ view: SectionView }>) {
  return (
    <section data-testid={`advisor-section-${view.id}`} className="mb-5">
      <h3 className="m-0 mb-2 text-body-02 font-medium text-black">{view.title}</h3>
      <div className="flex flex-col gap-3">
        {view.blocks.map((block, blockIndex) => (
          // Display tree is static per version — index keys are stable.
          <div key={blockIndex}>
            {block.title && (
              <div className="text-body-03 font-medium text-[#374151] mb-1">{block.title}</div>
            )}
            {block.rows.length > 0 && <RowList rows={block.rows} testIdPrefix={`advisor-${view.id}`} />}
            {(block.items ?? []).map((itemRows, itemIndex) => (
              <div
                key={itemIndex}
                className="mt-2 pl-3 border-l-2 border-[#E9E7FD]"
                data-testid={`advisor-${view.id}-item-${itemIndex}`}
              >
                <div className="text-body-04 text-[#6b7280] mb-1">#{itemIndex + 1}</div>
                <RowList rows={itemRows} testIdPrefix={`advisor-${view.id}-${itemIndex}`} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Read-only advisor view of a profile's living, versioned profile form
 * (fact-find / risk assessment). Sectioned (wizard DSL v2) versions render
 * grouped by section with clientRef values resolved to client names; flat
 * (DSL v1) versions keep the legacy label → value list. The advisor never
 * edits — clients own the form.
 */
export function ProfileFormTab({ profileId, formType, showResult }: Readonly<Props>) {
  const [schema, setSchema] = useState<FactFindSchema | null>(null);
  const [latest, setLatest] = useState<ProfileFormVersion | null>(null);
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, l] = await Promise.all([
        profileFormApi.getSchema(profileId, formType),
        profileFormApi.getLatest(profileId, formType),
      ]);
      setSchema(s);
      setLatest(l);
      if (isSectionedSchema(s)) {
        // clientRef / per-client blocks need names; tolerate a failure (ids
        // fall back to a generic "Client" label).
        try {
          const clients = await clientApi.getClientsByProfile(profileId);
          const names: Record<string, string> = {};
          for (const c of clients) {
            names[c.id] = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || 'Client';
          }
          setClientNames(names);
        } catch {
          setClientNames({});
        }
      }
    } catch {
      setError('Could not load this form.');
    } finally {
      setLoading(false);
    }
  }, [profileId, formType]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <div data-testid="profile-form-tab-loading" className="py-8 flex justify-center"><Spinner /></div>;
  }
  if (error || schema === null) {
    return (
      <div data-testid="profile-form-tab-error">
        <Alert variant="error">{error || 'Could not load this form.'}</Alert>
      </div>
    );
  }
  if (latest === null) {
    return (
      <Card>
        <div data-testid="profile-form-tab-empty" className="py-8 text-center text-[#6b7280] font-sans">
          The client has not completed this form yet.
        </div>
      </Card>
    );
  }

  const submitted = latest.submittedAt ? new Date(latest.submittedAt).toLocaleDateString() : '';
  const sectioned = isSectionedSchema(schema);
  const views = sectioned ? buildSectionViews(schema, latest.answers, clientNames) : [];
  const flatRows = sectioned
    ? []
    : (schema.questions ?? [])
        .map((q) => ({ q, value: latest.answers[q.id] }))
        .filter((r) => r.value != null && r.value !== '');
  const isEmpty = sectioned ? views.length === 0 : flatRows.length === 0;

  return (
    <Card>
      <div data-testid="profile-form-tab-readout" className="font-sans">
        {showResult && latest.result && <RiskResultCard result={latest.result as RiskResult} />}

        <div className="text-[13px] text-[#6b7280] mb-3">
          Version {latest.versionNo}
          {submitted ? ` · submitted ${submitted}` : ''}
        </div>

        {isEmpty && <p className="text-[#6b7280]">No answers were provided.</p>}

        {sectioned
          ? views.map((view) => <SectionBlocks key={view.id} view={view} />)
          : groupFlatRowsByTopic(flatRows).map((group) => (
              <section
                key={group.topic ?? 'flat'}
                data-testid={group.topic ? `advisor-topic-${group.topic}` : undefined}
                className={group.topic ? 'mb-5' : undefined}
              >
                {group.topic && (
                  <h3 className="m-0 mb-2 text-body-02 font-medium text-black">{group.topic}</h3>
                )}
                <dl className="grid grid-cols-[minmax(160px,240px)_1fr] gap-x-4 gap-y-2 m-0">
                  {group.rows.map(({ q, value }) => (
                    <React.Fragment key={q.id}>
                      <dt className="text-body-03 text-[#6b7280]">{q.label || q.id}</dt>
                      <dd className="text-body-03 m-0" data-testid={`advisor-answer-${q.id}`}>
                        {displayAnswer(q, value)}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            ))}
      </div>
    </Card>
  );
}
